import { Campaign, Client, Collab, Influencer, isInfluencerCancellableStatus, canInfluencerDeclineCollab, isDeliveryOnlyUgc } from '@foodfluence/shared';
import type { CardCta } from '@foodfluence/shared';
import { IconType } from 'react-icons';
import {
  FiEdit2, FiTrash2, FiMessageSquare, FiPackage, FiCalendar,
  FiCheck, FiGift, FiMapPin, FiUpload, FiClock, FiRepeat,
  FiAward, FiTruck, FiCheckSquare, FiChevronRight
} from 'react-icons/fi';
import { isShippingCampaignType, isInStoreCampaignType, isRetailCampaignType, isPreAcceptance, hasRecentExtensionRequest, hasRecentWaitlistRequest, needsExtensionRequest, isCampaignEnded, isBrandManagedCampaignType, isInStoreWithAvailableTimes, isShipToHomeCampaignType } from 'utils/collabUtils';
import { toDate } from '../../../utils/dateUtils';
import { getContentDueDateRaw, shouldShowContentDueReminder } from './dateUtils';
import { waitingForVerification, isUGC } from 'utils/deliverableUtils';
import { submissionPathForCollab } from './collabRoutes';
export { isUGC, hasDeliverableLinks } from 'utils/deliverableUtils';
export { hasRecentExtensionRequest, hasRecentWaitlistRequest, needsExtensionRequest, isCampaignEnded } from 'utils/collabUtils';

/**
 * Review page: Request Extension replaces Accept once go-live has passed — Accept is blocked
 * then, and asking the brand (who can move go-live) is the only way forward — and, before
 * the end date, when every slot is booked. Otherwise Accept opens the modal itself.
 */
export const requestExtensionReplacesAccept = (
  campaign: Campaign,
  collab: Collab,
  schedulingFullyBooked: boolean,
  postByPassed: boolean
): boolean => postByPassed || (!isCampaignEnded(campaign, collab) && schedulingFullyBooked);

export const isUnconfirmedCollab = (collab: Collab, campaign: Campaign): boolean => {
  return collab.status === 'pendingInfluencerApproval' || isAwaitingInfluencerConfirmation(collab, campaign) ;
};

export const isUnconfirmedCollabOrPendingAcceptance = (collab: Collab, campaign: Campaign): boolean => {
  return isUnconfirmedCollab(collab, campaign) || collab.status === 'pendingClientApproval';
};

/**
 * Checks if a collab is an accepted custom collab that is not scheduled,
 * or does not have shipping info, or does not have a posting date
 */
const isAwaitingInfluencerConfirmation = (collab: Collab, campaign: Campaign): boolean => {
  if (collab.status !== 'pendingRedemption') return false;

  if (campaign?.campaignType === 'inStore') return !collab.scheduledCollabDate && !collab.clientRequestedReschedule;
  if (isShippingCampaignType(campaign?.campaignType)) return !collab.shippingAddress && !collab.clientRequestedReschedule;
  if (campaign?.campaignType === 'retail') return !collab.scheduledPostingDate && !collab.clientRequestedReschedule;
  if (campaign?.campaignType === 'brandManaged') return false;
  return false;
};

export const isCampaignFull = (campaign: Campaign, remainingSpots: number, collab: Collab): boolean => {
  return campaign?.maxCollabs != null && remainingSpots != null && remainingSpots === 0 && isUnconfirmedCollab(collab, campaign);
};

/** Returns a hex color for spot urgency: green (≥7), orange (≥4), red (<4) */
export const getSpotColor = (spots: number): string => {
  if (spots >= 7) return '#22C55E';
  if (spots >= 4) return '#F59E0B';
  return '#EF4444';
};

const isSchedulingRequired = (collab: Collab, campaign: Campaign): boolean => {
  if (collab.status !== 'pendingRedemption') return false;
  if (isInStoreCampaignType(campaign.campaignType)) return !collab.scheduledCollabDate;
  if (isRetailCampaignType(campaign.campaignType)) return !collab.scheduledPostingDate;
  if (isShippingCampaignType(campaign.campaignType)) return true;
  return false;
};

const byRecency = (a: Collab, b: Collab): number => {
  const dateA = toDate(a.collabReceivedTime) || toDate(a.collabRequestedTime);
  const dateB = toDate(b.collabReceivedTime) || toDate(b.collabRequestedTime);
  return dateA && dateB ? dateB.getTime() - dateA.getTime() : 0;
};

export const getCurrentCollabs = (collabData: Collab[], campaignData?: Record<string, Campaign>): Collab[] => {
  return collabData
    .filter((collab: Collab) => {
      if (!['pendingSubmission', 'pendingShipment', 'pendingRedemption', 'collabRedeemed'].includes(collab.status)) return false;
      if (campaignData) {
        const campaign = campaignData[collab.id];
        if (campaign && isSchedulingRequired(collab, campaign)) return false;
      }
      return true;
    })
    .sort(byRecency);
};

export const getSchedulingRequiredCollabs = (collabData: Collab[], campaignData: Record<string, Campaign>): Collab[] => {
  return collabData
    .filter((collab: Collab) => {
      if (collab.status !== 'pendingRedemption') return false;
      const campaign = campaignData[collab.id];
      return campaign ? isSchedulingRequired(collab, campaign) : false;
    })
    .sort(byRecency);
};

  export const getPastCollabs = (collabData: Collab[]): Collab[] => {
    return collabData
      .filter((collab: Collab) => {
        return ['collabRejected', 'collabCompleted', 'collabExpired', 'collabCanceled', 'clientRejected', 'clientCanceled'].includes(collab.status)
      })
      .sort((a: Collab, b: Collab) => {
        const dateA = toDate(a.collabReceivedTime) || toDate(a.collabRequestedTime);
        const dateB = toDate(b.collabReceivedTime) || toDate(b.collabRequestedTime);
        return dateA && dateB ? dateB.getTime() - dateA.getTime() : 0;
      });
  };

  export const getPreviousTabSections = (collabData: Collab[]): {
    completed: Collab[];
    rejected: Collab[];
    canceled: Collab[];
    expired: Collab[];
  } => {
    const previous = getPastCollabs(collabData);
    return {
      completed: previous.filter(c => c.status === 'collabCompleted'),
      rejected: previous.filter(c => ['collabRejected', 'clientRejected'].includes(c.status)),
      canceled: previous.filter(c => ['collabCanceled', 'clientCanceled'].includes(c.status)),
      expired: previous.filter(c => c.status === 'collabExpired'),
    };
  };

export const getInvitesTabSections = (
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  spotsMap: Record<string, number>
): {
  waitlist: Collab[];
  extensions: Collab[];
  counters: Collab[];
  collabRequests: Collab[];
  sentRequests: Collab[];
} => {
  const seen = new Set<string>();

  const waitlist = collabs.filter(collab => {
    const campaign = campaignData[collab.id];
    if (!campaign) return false;
    const spots = spotsMap[collab.influencerCampaignID!];
    if (spots === undefined) return false;
    if (isCampaignFull(campaign, spots, collab)) {
      seen.add(collab.id);
      return true;
    }
    return false;
  });

  const extensions = collabs.filter(collab => {
    if (seen.has(collab.id)) return false;
    const campaign = campaignData[collab.id];
    if (!campaign) return false;
    if (needsExtensionRequest(campaign, collab)) {
      seen.add(collab.id);
      return true;
    }
    return false;
  });

  const counters = collabs.filter(collab => {
    if (seen.has(collab.id)) return false;
    if (collab.status === 'influencerCountered') {
      seen.add(collab.id);
      return true;
    }
    return false;
  });

  const collabRequests = collabs.filter(collab => {
    if (seen.has(collab.id)) return false;
    if (collab.status === 'pendingInfluencerApproval') {
      seen.add(collab.id);
      return true;
    }
    return false;
  });

  const sentRequests = collabs.filter(collab => {
    if (seen.has(collab.id)) return false;
    if (collab.status === 'pendingClientApproval') {
      seen.add(collab.id);
      return true;
    }
    return false;
  });

  const byRecency = (a: Collab, b: Collab): number => {
    const dateA = toDate(a.collabReceivedTime) || toDate(a.collabRequestedTime);
    const dateB = toDate(b.collabReceivedTime) || toDate(b.collabRequestedTime);
    return dateA && dateB ? dateB.getTime() - dateA.getTime() : 0;
  };

  return {
    waitlist: waitlist.sort(byRecency),
    extensions: extensions.sort(byRecency),
    counters: counters.sort(byRecency),
    collabRequests: collabRequests.sort(byRecency),
    sentRequests: sentRequests.sort(byRecency),
  };
};

  export const isInAppCredit = (collab?: Collab, campaign?: Campaign): boolean => {
    return !!collab && !!campaign && collab.credit > 0 && (!campaign.creditType || campaign.creditType === 'inApp');
  }

  export const isClientHandledCredit = (collab?: Collab, campaign?: Campaign): boolean => {
    return !!collab && !!campaign && collab.credit > 0 && campaign.creditType === 'clientHandled';
  }

/**
 * Checks if a collab can be rejected by the influencer
 */
export const canRejectCollab = (collabStatus: string): boolean => {
  // Delegates to the shared guard so the button and the /collabs/decline endpoint can't drift.
  return canInfluencerDeclineCollab(collabStatus);
};

/**
 * Checks if a collab can be canceled by the influencer
 */
export const canCancelCollab = (collabStatus: string): boolean => {
  // Status half of the shared cancel rule. The caller pairs this with the campaign-type and
  // pre-visit-cutoff checks (also shared) so the button and /collabs/cancel enforce the same
  // three rules.
  return isInfluencerCancellableStatus(collabStatus);
};


// ---------------------------------------------------------------------------
// Action descriptors — public types
// ---------------------------------------------------------------------------

export interface CollabActionDescriptor {
  icon: IconType;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface CollabActionHandlers {
  navigate: (path: string, options?: object) => void;
  openExtensionRequestModal: () => void;
  openDeleteRequestModal: () => void;
  openAddressModal: () => void;
  openConfirmAlreadyVisitedModal: () => void;
  openConfirmOrderReceivedModal: () => void;
  openMarkProductReceivedModal: () => void;
  openRedemptionInfoModal: () => void;
  onMessage: () => void;
  showToast: (message: string) => void;
  client?: Client;
}

export interface CollabNextStep {
  nextStepKey: string;
  instruction: string;
  icon: IconType;
  /** Absent when there is no primary CTA (e.g. reschedule locked). */
  onClick?: () => void;
}

// ---------------------------------------------------------------------------
// State map — internal types and data
// ---------------------------------------------------------------------------

type CollabStateKey =
  | 'campaignFull'
  | 'inStoreDatesAndTimesCampaignEnded'
  | 'needsExtension'
  | 'pendingClientApproval'
  | 'influencerCountered'
  | 'pendingRedemption_shipping'
  | 'pendingRedemption_brandManaged'
  | 'pendingRedemption_retail_noDate'
  | 'pendingRedemption_retail_hasDate'
  | 'pendingRedemption_inStore_inWindow'
  | 'pendingRedemption_inStore_afterWindow'
  | 'pendingRedemption_inStore_beforeWindow'
  | 'pendingRedemption_inStore_within24h'
  | 'pendingRedemption_inStore_noDate'
  | 'pendingRedemption_inStore_noDate_full'
  | 'pendingRedemption_inStore_noDate_ended'
  | 'pendingSubmission'
  | 'pendingSubmission_shipToHomeWaiting'
  | 'pendingVerification'
  | 'pendingResubmission'
  | 'collabRedeemed'
  | 'pendingShipment_notFulfilled'
  | 'pendingShipment_fulfilled';

interface CollabActionConfig {
  icon: IconType;
  getLabel: (collab: Collab, campaign: Campaign) => string;
  getOnClick: (
    handlers: CollabActionHandlers,
    collab: Collab,
    campaign: Campaign,
    influencerData: Influencer | null
  ) => () => void;
  isDisabled?: (collab: Collab, campaign: Campaign) => boolean;
}

interface CollabStateConfig {
  primaryAction?: CollabActionConfig;
  secondaryAction?: CollabActionConfig;
  tertiaryAction?: CollabActionConfig;
  nextStepKey: string;
  instruction: string;
  /** Replaces `instruction` for delivery-only UGC (isDeliveryOnlyUgc), which is never posted. */
  deliveryOnlyInstruction?: string;
  nextStepIcon: IconType;
}

const submitContentOnClick = (
  handlers: CollabActionHandlers,
  collab: Collab
): (() => void) => () => {
  handlers.navigate(submissionPathForCollab(collab));
};

/**
 * Shipped-product collabs still awaiting delivery: creators usually have the product before
 * tracking says so, so let them submit straight from the collab page instead of only from
 * behind the order-status screen. Confirming receipt is what advances the collab, hence the
 * modal — same operation as "I've Received My Order" on the order-status screen.
 */
const submitContentOnReceiptAction: CollabActionConfig = {
  icon: FiUpload,
  getLabel: () => 'Submit Content',
  getOnClick: (handlers) => () => handlers.openConfirmOrderReceivedModal(),
};

const COLLAB_STATE_MAP: Record<CollabStateKey, CollabStateConfig> = {
  campaignFull: {
    primaryAction: {
      icon: FiClock,
      getLabel: (collab) => hasRecentWaitlistRequest(collab) ? 'Waitlist Requested' : 'Request Waitlist',
      getOnClick: (handlers) => () => handlers.openExtensionRequestModal(),
      isDisabled: (collab) => hasRecentWaitlistRequest(collab),
    },
    nextStepKey: 'requestWaitlist',
    instruction: 'This campaign is full — join the waitlist to stay in line',
    nextStepIcon: FiClock,
  },
  inStoreDatesAndTimesCampaignEnded: {
    secondaryAction: {
      icon: FiTrash2,
      getLabel: () => 'Delete Collab',
      getOnClick: (handlers) => () => handlers.openDeleteRequestModal(),
    },
    nextStepKey: 'inStoreDatesAndTimesCampaignEnded',
    instruction: 'All available dates for this campaign have passed',
    nextStepIcon: FiClock,
  },
  needsExtension: {
    primaryAction: {
      icon: FiClock,
      getLabel: (collab) => hasRecentExtensionRequest(collab) ? 'Extension Requested' : 'Request Extension',
      getOnClick: (handlers) => () => handlers.openExtensionRequestModal(),
      isDisabled: (collab) => hasRecentExtensionRequest(collab),
    },
    secondaryAction: {
      icon: FiTrash2,
      getLabel: () => 'Delete Collab',
      getOnClick: (handlers) => () => handlers.openDeleteRequestModal(),
    },
    nextStepKey: 'requestExtension',
    instruction: 'Your collab period has ended — request an extension',
    nextStepIcon: FiClock,
  },
  pendingClientApproval: {
    primaryAction: {
      icon: FiEdit2,
      getLabel: () => 'Edit Request',
      getOnClick: (handlers, collab, campaign) => () =>
        handlers.navigate('/collabs/request-collab', { state: { requestedCollab: collab, campaign, client: handlers.client } }),
    },
    secondaryAction: {
      icon: FiTrash2,
      getLabel: () => 'Delete Request',
      getOnClick: (handlers) => () => handlers.openDeleteRequestModal(),
    },
    nextStepKey: 'waitingBrandApproval',
    instruction: "Your request is in — waiting for the brand to approve",
    nextStepIcon: FiClock,
  },
  influencerCountered: {
    primaryAction: {
      icon: FiEdit2,
      getLabel: () => 'Edit Counter',
      getOnClick: (handlers, collab, campaign, influencerData) => () =>
        handlers.navigate('/collabs/counter-collab', { state: { collab, campaign, influencer: influencerData } }),
    },
    nextStepKey: 'waitingBrandCounter',
    instruction: "Counter sent — waiting for the brand to respond",
    nextStepIcon: FiClock,
  },
  pendingRedemption_shipping: {
    primaryAction: {
      icon: FiPackage,
      getLabel: () => 'Add Details',
      getOnClick: (handlers) => () => handlers.openAddressModal(),
    },
    nextStepKey: 'orderProduct',
    instruction: "Now that the collab has been accepted, enter your shipping address",
    nextStepIcon: FiPackage,
  },
  pendingRedemption_brandManaged: {
    nextStepKey: 'messageBrand',
    instruction: "Head to your Inbox to view and respond to messages from the brand. They will coordinate next steps with you directly.",
    nextStepIcon: FiMessageSquare,
  },
  pendingRedemption_retail_noDate: {
    primaryAction: {
      icon: FiCalendar,
      getLabel: () => 'Schedule Retail Collab',
      getOnClick: (handlers, collab) => () =>
        handlers.navigate('/collabs/schedule-retail', { state: { collabId: collab.id } }),
    },
    nextStepKey: 'scheduleRetailCollab',
    instruction: "Now that the collab has been accepted, schedule your posting date",
    deliveryOnlyInstruction: "Now that the collab has been accepted, schedule your delivery date",
    nextStepIcon: FiCalendar,
  },
  pendingRedemption_retail_hasDate: {
    primaryAction: {
      icon: FiCheck,
      getLabel: (collab, campaign) => (isDeliveryOnlyUgc(collab, campaign) ? 'Ready to Submit?' : 'Ready to Post?'),
      // The redeem itself, not a trip back to scheduling. The store and posting date were settled
      // at acceptance; re-asking for them here is what a creator reads as the app refusing to let
      // them upload. Confirm, advance, and land on submission — the same thing every other
      // "I'm done, let me submit" action on this page does.
      getOnClick: (handlers) => () => handlers.openConfirmOrderReceivedModal(),
    },
    nextStepKey: 'readyToPost',
    instruction: "Your posting date is set — ready to share?",
    deliveryOnlyInstruction: "Your delivery date is set — ready to submit?",
    nextStepIcon: FiCheck,
  },
  pendingRedemption_inStore_inWindow: {
    primaryAction: {
      icon: FiGift,
      getLabel: () => "Redeem Now!",
      getOnClick: (handlers, collab) => () =>
        handlers.navigate(`/collabs/redeem-reward?id=${collab.id}`),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'redeemReward',
    instruction: "Your visit is now — time to redeem your reward!",
    nextStepIcon: FiGift,
  },
  pendingRedemption_inStore_afterWindow: {
    primaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'markVisitComplete',
    instruction: "Your visit window has passed — did you go?",
    nextStepIcon: FiMapPin,
  },
  pendingRedemption_inStore_beforeWindow: {
    primaryAction: {
      icon: FiCalendar,
      getLabel: () => 'Reschedule Collab',
      getOnClick: (handlers, collab, campaign, influencerData) => () =>
        handlers.navigate('/collabs/schedule-collab', {
          state: { collabId: collab.id, campaign, influencer: influencerData }
        }),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    tertiaryAction: {
      icon: FiGift,
      getLabel: () => "Redeem Now!",
      getOnClick: (handlers) => () =>
        handlers.openRedemptionInfoModal()
    }, 
    nextStepKey: 'visitScheduled',
    instruction: "Your visit is scheduled — we'll see you soon!",
    nextStepIcon: FiCalendar,
  },
  pendingRedemption_inStore_within24h: {
    primaryAction: {
      icon: FiCalendar,
      getLabel: () => 'Reschedule Collab',
      getOnClick: (handlers, collab, campaign, influencerData) => () =>
        handlers.navigate('/collabs/schedule-collab', {
          state: { collabId: collab.id, campaign, influencer: influencerData }
        }),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    tertiaryAction: {
      icon: FiGift,
      getLabel: () => "Redeem Now!",
      getOnClick: (handlers) => () =>
        handlers.openRedemptionInfoModal()
    },
    nextStepKey: 'visitScheduled',
    instruction: "Your visit is coming up — rescheduling and cancelling are no longer available.",
    nextStepIcon: FiCalendar,
  },
  pendingRedemption_inStore_noDate: {
    primaryAction: {
      icon: FiCalendar,
      getLabel: () => 'Schedule Collab',
      getOnClick: (handlers, collab, campaign, influencerData) => () =>
        handlers.navigate('/collabs/schedule-collab', {
          state: { collabId: collab.id, campaign, influencer: influencerData }
        }),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'scheduleCollabInStore',
    instruction: "You're not confirmed until you schedule your visit — pick a time to lock in your spot.",
    nextStepIcon: FiCalendar,
  },
  // Accepted in-store collab, not yet scheduled, in a FULL campaign: keep the waitlist affordance
  // (so it's clear the spot isn't confirmed) but still let someone who already visited submit.
  pendingRedemption_inStore_noDate_full: {
    primaryAction: {
      icon: FiClock,
      getLabel: (collab) => hasRecentWaitlistRequest(collab) ? 'Waitlist Requested' : 'Request Waitlist',
      getOnClick: (handlers) => () => handlers.openExtensionRequestModal(),
      isDisabled: (collab) => hasRecentWaitlistRequest(collab),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'campaignFullSubmitOrWaitlist',
    instruction: "This campaign is full — request the waitlist to claim a spot. Already came in? Submit your content below.",
    nextStepIcon: FiClock,
  },
  // Accepted in-store collab, not yet scheduled: extension when dates passed or slots are full.
  pendingRedemption_inStore_noDate_ended: {
    primaryAction: {
      icon: FiClock,
      getLabel: (collab) => hasRecentExtensionRequest(collab) ? 'Extension Requested' : 'Request Extension',
      getOnClick: (handlers) => () => handlers.openExtensionRequestModal(),
      isDisabled: (collab) => hasRecentExtensionRequest(collab),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'datesPassedSubmitOrExtension',
    instruction: 'Request an extension to continue. Already came in? Submit your content below.',
    nextStepIcon: FiClock,
  },
  pendingSubmission: {
    primaryAction: {
      icon: FiUpload,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers, collab) => submitContentOnClick(handlers, collab),
    },
    nextStepKey: 'submitContent',
    instruction: "Last step — time to submit your content!",
    nextStepIcon: FiUpload,
  },
  // Ship-to-home with no clock yet: the due date starts when the brand ships or the creator
  // reports receipt. "Received" leads; submitting stays available for creators who already filmed.
  pendingSubmission_shipToHomeWaiting: {
    primaryAction: {
      icon: FiPackage,
      getLabel: () => 'I received my product',
      getOnClick: (handlers) => () => handlers.openMarkProductReceivedModal(),
    },
    secondaryAction: {
      icon: FiUpload,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers, collab) => submitContentOnClick(handlers, collab),
    },
    nextStepKey: 'productReceived',
    instruction: "Tap \"I received my product\" once it arrives to start your content deadline",
    nextStepIcon: FiPackage,
  },
  pendingVerification: {
    primaryAction: {
      icon: FiClock,
      getLabel: () => 'Awaiting Verification',
      getOnClick: (handlers, collab) => submitContentOnClick(handlers, collab),
      isDisabled: () => false,
    },
    nextStepKey: 'awaitingVerification',
    instruction: "Content submitted — waiting for review",
    nextStepIcon: FiClock,
  },
  pendingResubmission: {
    primaryAction: {
      icon: FiRepeat,
      getLabel: () => 'Resubmit Content',
      getOnClick: (handlers, collab) => submitContentOnClick(handlers, collab),
    },
    nextStepKey: 'resubmitContent',
    instruction: "The brand requested changes — resubmit your content",
    nextStepIcon: FiRepeat,
  },
  collabRedeemed: {
    primaryAction: {
      icon: FiAward,
      getLabel: () => 'View Reward',
      getOnClick: (handlers, collab) => () =>
        handlers.navigate(`/collabs/view-reward?id=${collab.id}`),
    },
    secondaryAction: {
      icon: FiMapPin,
      getLabel: () => 'Submit Content',
      getOnClick: (handlers) => () => handlers.openConfirmAlreadyVisitedModal(),
    },
    nextStepKey: 'viewReward',
    instruction: "Your credit is ready — tap to view your reward",
    nextStepIcon: FiAward,
  },
  pendingShipment_notFulfilled: {
    primaryAction: {
      icon: FiTruck,
      getLabel: () => 'View Order Status',
      getOnClick: (handlers, collab) => () =>
        handlers.navigate(`/collabs/pending-shipment?id=${collab.id}`),
    },
    secondaryAction: submitContentOnReceiptAction,
    nextStepKey: 'viewOrderStatus',
    instruction: "Your order is being worked on - track it's status",
    nextStepIcon: FiTruck,
  },
  pendingShipment_fulfilled: {
    primaryAction: {
      icon: FiCheckSquare,
      getLabel: () => 'Confirm Delivery',
      getOnClick: (handlers, collab) => () =>
        handlers.navigate(`/collabs/pending-shipment?id=${collab.id}`),
    },
    secondaryAction: submitContentOnReceiptAction,
    nextStepKey: 'confirmDelivery',
    instruction: "Your product is on its way — confirm when you receive it",
    nextStepIcon: FiCheckSquare,
  },
};

/**
 * Derives the current state key for a collab, encapsulating all branching logic
 * in one place so getCollabActions and getCollabNextStep can both use it.
 */
/**
 * Ship-to-home collab whose content clock hasn't started: no due date yet (neither shipping
 * confirmation nor the creator's receipt has stamped one). Legacy collabs with no locked
 * turnaround never get a clock, so they never see the action.
 */
export const needsShipToHomeReceived = (collab: Collab, campaign: Campaign): boolean =>
  collab?.status === 'pendingSubmission' &&
  isShipToHomeCampaignType(campaign?.campaignType) &&
  !!collab.contentTurnaroundDays &&
  !getContentDueDateRaw(collab, campaign);

const getCollabStateKey = (
  collab: Collab,
  campaign: Campaign,
  remainingSpots: number | null,
  schedulingFullyBooked = false
): CollabStateKey | null => {
  // In-store collabs whose request was accepted but not yet scheduled always keep a "Submit Content"
  // button (so anyone who already visited can submit), but the partner action + banner make the
  // situation clear: waitlist when the campaign is full, extension when dates passed or slots are full,
  // schedule otherwise. Submitting confirms the spot.
  const inStoreAwaitingSchedule =
    collab.status === 'pendingRedemption' &&
    isInStoreCampaignType(campaign?.campaignType) &&
    !collab.scheduledCollabDate &&
    !collab.clientRequestedReschedule;

  if (inStoreAwaitingSchedule) {
    if (isCampaignFull(campaign, remainingSpots, collab)) return 'pendingRedemption_inStore_noDate_full';
    if (needsExtensionRequest(campaign, collab, schedulingFullyBooked)) return 'pendingRedemption_inStore_noDate_ended';
    // otherwise fall through to the switch → 'pendingRedemption_inStore_noDate' (Schedule + Submit)
  } else {
    if (isCampaignFull(campaign, remainingSpots, collab)) return 'campaignFull';
    if (needsExtensionRequest(campaign, collab) && isInStoreWithAvailableTimes(collab, campaign)) return 'inStoreDatesAndTimesCampaignEnded';
    if (needsExtensionRequest(campaign, collab)) return 'needsExtension';
  }

  switch (collab.status) {
    case 'pendingClientApproval':
      return 'pendingClientApproval';

    case 'influencerCountered':
      return 'influencerCountered';

    case 'pendingRedemption': {
      if (isShippingCampaignType(campaign?.campaignType)) return 'pendingRedemption_shipping';
      if (isBrandManagedCampaignType(campaign?.campaignType)) return 'pendingRedemption_brandManaged';
      if (isRetailCampaignType(campaign?.campaignType)) {
        // The location counts as much as the date. "Ready to Post?" redeems on the spot now, and
        // `acceptRetailCollab`/`handleReadyToPost` always refused a collab with no pickup store on
        // a campaign that has them — skipping straight to submission would let content be filed,
        // and reimbursed, against no store at all. Six such collabs exist. Send them to scheduling,
        // which is the screen that collects both.
        const owesLocation =
          (campaign?.foodfluenceLocations?.length ?? 0) > 0 && !collab.selectedLocation;
        return collab.scheduledPostingDate && !owesLocation
          ? 'pendingRedemption_retail_hasDate'
          : 'pendingRedemption_retail_noDate';
      }
      // inStore
      const scheduledDate = toDate(collab?.scheduledCollabDate);
      if (!scheduledDate) return 'pendingRedemption_inStore_noDate';
      const now = new Date();
      const minus3h = new Date(scheduledDate.getTime() - 3 * 60 * 60 * 1000);
      const plus3h = new Date(scheduledDate.getTime() + 3 * 60 * 60 * 1000);
      const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      if (now >= minus3h && now <= plus3h) return 'pendingRedemption_inStore_inWindow';
      if (now > plus3h) return 'pendingRedemption_inStore_afterWindow';
      if (scheduledDate <= in24h) return 'pendingRedemption_inStore_within24h';
      return 'pendingRedemption_inStore_beforeWindow';
    }

    case 'pendingSubmission':
      if (waitingForVerification(collab)) return 'pendingVerification';
      if (needsShipToHomeReceived(collab, campaign)) return 'pendingSubmission_shipToHomeWaiting';
      return 'pendingSubmission';

    case 'pendingResubmission':
      return 'pendingResubmission';

    case 'collabRedeemed':
      return 'collabRedeemed';

    case 'pendingShipment':
      return collab.orderFulfilledTime ? 'pendingShipment_fulfilled' : 'pendingShipment_notFulfilled';

    default:
      return null;
  }
};

/**
 * Determines if the Message action should be shown for a collab.
 * Hides Message for invite-section collabs until shipping is confirmed or they're scheduled, and
 * for pre-acceptance collabs other than an open invite.
 */
const shouldShowMessageAction = (
  collab: Collab,
  campaign: Campaign,
  remainingSpots: number | null
): boolean => {
  if (!campaign) return true;

  // Pre-collab messaging: a creator may open a chat on an open invite (the first send creates the
  // thread server-side); other pre-acceptance states have no creator-started chat.
  if (isPreAcceptance(collab)) return collab.status === 'pendingInfluencerApproval';

  if (isAwaitingInfluencerConfirmation(collab, campaign)) {
    return false;
  }

  return true;
};

/**
 * Returns the list of action descriptors for a collab based on its current status.
 * Handlers are injected so this function has no React or router dependencies.
 */
export const getCollabActions = (
  collab: Collab,
  campaign: Campaign,
  influencerData: Influencer | null,
  remainingSpots: number | null,
  handlers: CollabActionHandlers,
  schedulingFullyBooked = false
): CollabActionDescriptor[] => {
  const key = getCollabStateKey(collab, campaign, remainingSpots, schedulingFullyBooked);
  const actions: CollabActionDescriptor[] = [];

  if (key) {
    const config = COLLAB_STATE_MAP[key];
    // A reschedule needs somewhere to move to: when scheduling is fully booked there are no
    // other slots, so grey out the Reschedule button (the creator keeps their existing date).
    const rescheduleBlockedByNoSlots =
      schedulingFullyBooked && key === 'pendingRedemption_inStore_beforeWindow';
    if (config.primaryAction) {
      actions.push({
        icon: config.primaryAction.icon,
        label: config.primaryAction.getLabel(collab, campaign),
        onClick: config.primaryAction.getOnClick(handlers, collab, campaign, influencerData),
        disabled: (config.primaryAction.isDisabled?.(collab, campaign) ?? false) || rescheduleBlockedByNoSlots,
      });
    }
    if (config.secondaryAction) {
      actions.push({
        icon: config.secondaryAction.icon,
        label: config.secondaryAction.getLabel(collab, campaign),
        onClick: config.secondaryAction.getOnClick(handlers, collab, campaign, influencerData),
        disabled: config.secondaryAction.isDisabled?.(collab, campaign),
      });
    }
    if (config.tertiaryAction) {
      actions.push({
        icon: config.tertiaryAction.icon,
        label: config.tertiaryAction.getLabel(collab, campaign),
        onClick: config.tertiaryAction.getOnClick(handlers, collab, campaign, influencerData),
        disabled: config.tertiaryAction.isDisabled?.(collab, campaign),
      });
    }
  }

  if (shouldShowMessageAction(collab, campaign, remainingSpots)) {
    actions.push({ icon: FiMessageSquare, label: 'Message', onClick: handlers.onMessage });
  }
  return actions;
};

/**
 * Returns the single most-important next step for the influencer to take,
 * with a contextual instruction string and the appropriate icon.
 * Returns null when there is no meaningful next step (e.g. collab is complete).
 */
export const getCollabNextStep = (
  collab: Collab,
  campaign: Campaign,
  influencerData: Influencer | null,
  remainingSpots: number | null,
  handlers: CollabActionHandlers,
  schedulingFullyBooked = false
): CollabNextStep | null => {
  const key = getCollabStateKey(collab, campaign, remainingSpots, schedulingFullyBooked);
  if (!key) return null;

  const config = COLLAB_STATE_MAP[key];
  return {
    nextStepKey: config.nextStepKey,
    instruction: (isDeliveryOnlyUgc(collab, campaign) && config.deliveryOnlyInstruction) || config.instruction,
    icon: config.nextStepIcon,
    onClick: config.primaryAction?.getOnClick(handlers, collab, campaign, influencerData),
  };
};


// ---------------------------------------------------------------------------
// Content Due UI state
// ---------------------------------------------------------------------------

export type ContentDueUIState = 'countdown' | 'countdownBrandManaged' | 'brandManagedSchedulingNotice' | 'shipToHomeWaiting' | null;

export const getContentDueUIState = (collab: Collab, campaign: Campaign): ContentDueUIState => {
  const isBrandManaged = isBrandManagedCampaignType(campaign?.campaignType);
  const hasDueDate = !!getContentDueDateRaw(collab, campaign);
  if (shouldShowContentDueReminder(collab, campaign) && hasDueDate) {
    return isBrandManaged ? 'countdownBrandManaged' : 'countdown';
  }
  if (isBrandManaged) return 'brandManagedSchedulingNotice';
  if (needsShipToHomeReceived(collab, campaign)) return 'shipToHomeWaiting';
  return null;
};

// Re-export shared home page card types for convenience
export type { CardCta, CollabTask } from '@foodfluence/shared';

// ---------------------------------------------------------------------------
// Home Page — CTA helpers
// ---------------------------------------------------------------------------

export const getCampaignTypeLabel = (campaignType?: string): string => {
  switch (campaignType) {
    case 'inStore': return 'In Store';
    case 'retail': return 'Retail';
    case 'shopify': return 'Ship to Home';
    case 'shipToHome': return 'Ship to Home';
    default: return '';
  }
};

export const getNeedToConfirmCTA = (collab: Collab, campaign: Campaign): CardCta => {
  if (isShippingCampaignType(campaign?.campaignType)) {
    return { label: 'Confirm', variant: 'green' };
  }
  return { label: 'Schedule', variant: 'green' };
};

export const getWaitlistCTA = (collab: Collab): CardCta => {
  return hasRecentWaitlistRequest(collab)
    ? { label: 'Waitlist Requested', variant: 'gray' }
    : { label: 'Request Waitlist', variant: 'red' };
};

export const getExtensionCTA = (collab: Collab, campaign: Campaign): CardCta => {
  if (isInStoreWithAvailableTimes(collab, campaign)) return { label: '', variant: 'arrow' };
  return hasRecentExtensionRequest(collab)
    ? { label: 'Extension Requested', variant: 'gray' }
    : { label: 'Request Extension', variant: 'red' };
};

/** Whether to show the spots badge/CTA for a collab given remaining spots */
export const showSpotsBadge = (
  collab: Collab,
  campaign: Campaign,
  remainingSpots: number | null | undefined
): boolean => {
  return (
    campaign?.maxCollabs != null &&
    remainingSpots != null &&
    isUnconfirmedCollab(collab, campaign) &&
    !isCampaignFull(campaign, remainingSpots, collab)
  );
};

/**
 * Returns the offer expiry date if the collab offer is still pending and not yet expired. Follows
 * `collabOfferExpiresAt` alone, with or without a phone number; the server decides which offers get one.
 */
export const collabCanBeExpired = (collab: Collab): Date | undefined => {
  const offerExpiry = toDate(collab.collabOfferExpiresAt);
  if (!offerExpiry) return undefined;
  if (collab.status !== 'pendingInfluencerApproval') return undefined;
  if (offerExpiry.getTime() <= Date.now()) return undefined;
  return offerExpiry;
};

/** Returns a CardCta for remaining spots */
export const getSpotsCTA = (remainingSpots: number): CardCta => ({
  label: 'Limited Spots',
  variant: 'spots',
  spotsCount: remainingSpots,
});

// Re-export for convenience (FiChevronRight is used by the Next Step card UI)
export { FiChevronRight };
